import {
	Button,
	Grid2,
	Stack,
	styled,
	TablePagination,
	TextField,
	Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { MAIN_THEME_COLOR } from '../../assets/theme/Colors';
import { useMainCustomStyle } from '../../assets/theme/Theme';
import SideNavigationBar from '../../components/SideNavigationBar';
import ConfirmationPopup from '../../components/ConfirmationPopup';
import { FACEBOOK_CHAT_NAVIGATION_EVENT } from '../../Utils/NavigationGuard';
import FacebookPostChatsView from './components/FacebookPostChatsView';
import FacebookPostPopup from './components/FacebookPostPopup';
import FacebookPostsTable from './components/FacebookPostsTable';
import { ChatData, FacebookPost, FacebookPostConversation } from '../../types';
import {
	deleteFacebookPost,
	fetchFacebookPostConversations,
	fetchFacebookPosts,
	saveFacebookPost,
	sendFacebookConsultantMessage,
	updateFacebookBotMessage,
	updateFacebookConsultantMode,
} from '../../services/FacebookPostService';

function FacebookManager() {
	const mainDivStyle = useMainCustomStyle();
	const [facebookPosts, setFacebookPosts] = useState<Array<FacebookPost>>([]);
	const [totalPosts, setTotalPosts] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [page, setPage] = useState(0);
	const [selectedPost, setSelectedPost] = useState<FacebookPost | null>(null);
	const [selectedChatPost, setSelectedChatPost] =
		useState<FacebookPost | null>(null);
	const [selectedConversation, setSelectedConversation] =
		useState<FacebookPostConversation | null>(null);
	const [editingPost, setEditingPost] = useState<FacebookPost | null>(null);
	const [deletingPost, setDeletingPost] = useState<FacebookPost | null>(null);
	const [isLoadingPosts, setIsLoadingPosts] = useState(true);
	const [isSavingPost, setIsSavingPost] = useState(false);
	const [isDeletingPost, setIsDeletingPost] = useState(false);
	const [loadingChatPostId, setLoadingChatPostId] = useState('');
	const [isUpdatingBotMessage, setIsUpdatingBotMessage] = useState(false);
	const [isTogglingConsultantMode, setIsTogglingConsultantMode] =
		useState(false);
	const [isSendingConsultantMessage, setIsSendingConsultantMessage] =
		useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [isCreateMode, setIsCreateMode] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const isTableActionLoading =
		isSavingPost || isDeletingPost || Boolean(loadingChatPostId);

	useEffect(() => {
		loadFacebookPosts();
	}, [page, rowsPerPage, searchQuery]);

	useEffect(() => {
		function onSidebarNavigation(event: Event) {
			const route = (event as CustomEvent<{ route: string }>).detail
				?.route;

			if (route === '/social/facebook') {
				onCloseChats();
			}
		}

		window.addEventListener(
			FACEBOOK_CHAT_NAVIGATION_EVENT,
			onSidebarNavigation,
		);

		return () => {
			window.removeEventListener(
				FACEBOOK_CHAT_NAVIGATION_EVENT,
				onSidebarNavigation,
			);
		};
	}, []);

	async function loadFacebookPosts() {
		setIsLoadingPosts(true);
		setErrorMessage('');

		const response = await fetchFacebookPosts({
			page,
			limit: rowsPerPage,
			search: searchQuery,
		});

		if (response.error || !response.data) {
			setErrorMessage('Unable to load Facebook posts');
			setFacebookPosts([]);
			setTotalPosts(0);
		} else {
			setFacebookPosts(response.data.posts);
			setTotalPosts(response.data.total);
		}

		setIsLoadingPosts(false);
	}

	function handleChangePage(
		event: React.MouseEvent<HTMLButtonElement> | null,
		newPage: number,
	) {
		setPage(newPage);
	}

	function handleChangeRowsPerPage(
		event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
	) {
		setRowsPerPage(+event.target.value);
		setPage(0);
	}

	function onCreatePost() {
		setIsCreateMode(true);
		setEditingPost({
			id: '',
			source_id: getNextPostId(),
			name: '',
			description: '',
			media: [],
			welcomeMessages: { english: '', sinhala: '', tamil: '' },
			postUrl: '',
		});
	}

	function onEditPost(post: FacebookPost) {
		setIsCreateMode(false);
		setEditingPost({ ...post });
	}

	function onRequestDeletePost(post: FacebookPost) {
		setDeletingPost(post);
	}

	async function onConfirmDeletePost() {
		if (!deletingPost) {
			return;
		}

		setIsDeletingPost(true);
		try {
			const response = await deleteFacebookPost(deletingPost.id);

			if (response.error) {
				setErrorMessage('Unable to delete Facebook post');
				return;
			}

			setDeletingPost(null);
			await loadFacebookPosts();
		} finally {
			setIsDeletingPost(false);
		}
	}

	function onCloseChats() {
		setSelectedChatPost(null);
		setSelectedConversation(null);
	}

	async function onViewChats(post: FacebookPost) {
		setErrorMessage('');

		if (!post.id) {
			setErrorMessage('Unable to load chats for this Facebook post');
			return;
		}

		setLoadingChatPostId(post.id);
		try {
			const response = await fetchFacebookPostConversations(post.id);

			if (response.error || !response.data) {
				setErrorMessage('Unable to load WhatsApp conversations');
				return;
			}

			const postWithConversations = {
				...post,
				whatsappConversations: response.data,
			};

			setFacebookPosts(posts =>
				posts.map(item =>
					item.id === post.id ? postWithConversations : item,
				),
			);
			setSelectedChatPost(postWithConversations);
			setSelectedConversation(response.data[0] || null);
		} finally {
			setLoadingChatPostId('');
		}
	}

	async function onSavePost() {
		if (!editingPost) {
			return;
		}

		setIsSavingPost(true);
		try {
			const response = await saveFacebookPost(editingPost, isCreateMode);

			if (response.error) {
				setErrorMessage('Unable to save Facebook post');
				onClosePostForm();
				return;
			}

			onClosePostForm();
			await loadFacebookPosts();
		} finally {
			setIsSavingPost(false);
		}
	}

	async function onUpdateBotMessage(
		conversationId: string,
		date: string,
		messageIndex: number,
		message: string,
	) {
		if (!selectedChatPost) {
			return;
		}

		setIsUpdatingBotMessage(true);
		try {
			const response = await updateFacebookBotMessage(
				selectedChatPost.id,
				conversationId,
				date,
				messageIndex,
				message,
			);

			if (response.error || !response.data) {
				setErrorMessage('Unable to update bot message');
				return;
			}

			applyUpdatedPost(response.data, conversationId);
		} finally {
			setIsUpdatingBotMessage(false);
		}
	}

	async function onToggleConsultantMode(
		conversationId: string,
		isConsultantModeOn: boolean,
	) {
		if (!selectedChatPost) {
			return;
		}

		setIsTogglingConsultantMode(true);
		try {
			const response = await updateFacebookConsultantMode(
				selectedChatPost.id,
				conversationId,
				isConsultantModeOn,
			);

			if (response.error || !response.data) {
				setErrorMessage('Unable to update consultant mode');
				return;
			}

			applyUpdatedPost(response.data, conversationId);
		} finally {
			setIsTogglingConsultantMode(false);
		}
	}

	async function onSendConsultantMessage(
		conversationId: string,
		message: ChatData,
	) {
		if (!selectedChatPost) {
			return;
		}

		setIsSendingConsultantMessage(true);
		try {
			const response = await sendFacebookConsultantMessage(
				selectedChatPost.id,
				conversationId,
				message,
			);

			if (response.error || !response.data) {
				setErrorMessage('Unable to send consultant message');
				return;
			}

			applyUpdatedPost(response.data, conversationId);
		} finally {
			setIsSendingConsultantMessage(false);
		}
	}

	function applyUpdatedPost(
		updatedPost: FacebookPost,
		conversationId: string,
	) {
		const updatedConversation =
			updatedPost.whatsappConversations?.find(
				conversation => conversation.id === conversationId,
			) || null;

		setFacebookPosts(posts =>
			posts.map(post =>
				post.id === updatedPost.id ? updatedPost : post,
			),
		);
		setSelectedChatPost(updatedPost);
		setSelectedConversation(updatedConversation);
	}

	function onClosePostForm() {
		setIsCreateMode(false);
		setEditingPost(null);
	}

	function getNextPostId() {
		const lastIdNumber = facebookPosts.reduce((lastId, post) => {
			if (!post.source_id) return lastId;
			const idNumber = Number(post.source_id.replace('FB-', ''));
			return Number.isNaN(idNumber) ? lastId : Math.max(lastId, idNumber);
		}, 1000);

		return `FB-${lastIdNumber + 1}`;
	}

	function onSearchPosts(event: React.ChangeEvent<HTMLInputElement>) {
		setSearchQuery(event.target.value);
		setPage(0);
	}

	return (
		<>
			<SideNavigationBar />
			<div style={{ ...mainDivStyle, paddingTop: '36px' }}>
				{selectedChatPost ? (
					<FacebookPostChatsView
						post={selectedChatPost}
						selectedConversation={selectedConversation}
						onSelectConversation={setSelectedConversation}
						onUpdateBotMessage={onUpdateBotMessage}
						onToggleConsultantMode={onToggleConsultantMode}
						onSendConsultantMessage={onSendConsultantMessage}
						isUpdatingBotMessage={isUpdatingBotMessage}
						isTogglingConsultantMode={isTogglingConsultantMode}
						isSendingConsultantMessage={isSendingConsultantMessage}
						onClose={onCloseChats}
					/>
				) : (
					<Grid2 container paddingLeft="20px" paddingRight="20px">
						<Grid2
							container
							sx={{ paddingLeft: '10px', width: '100%' }}>
							<Stack
								direction="row"
								justifyContent="space-between"
								alignItems="center"
								sx={{ marginBottom: '14px', width: '100%' }}>
								<Typography
									fontSize="20px"
									fontWeight={700}
									color="#1F2933">
									Facebook Post Manager
								</Typography>
								<Stack
									direction="row"
									alignItems="center"
									spacing={1.5}>
									<TextField
										placeholder="Search by ID or name..."
										value={searchQuery}
										onChange={onSearchPosts}
										size="small"
										sx={{
											width: '240px',
											'& .MuiOutlinedInput-root': {
												fontSize: '13px',
												borderRadius: '8px',
											},
										}}
									/>
									<PrimaryButton
										disabled={isTableActionLoading}
										onClick={onCreatePost}>
										Create Post
									</PrimaryButton>
								</Stack>
							</Stack>
							{errorMessage && (
								<Typography
									color="error"
									fontSize="13px"
									marginBottom="10px">
									{errorMessage}
								</Typography>
							)}
							{isLoadingPosts ? (
								<Typography fontSize="14px">
									Loading Facebook posts...
								</Typography>
							) : (
								<>
									<PostsScrollView>
										<FacebookPostsTable
											posts={facebookPosts}
											onViewPost={setSelectedPost}
											onEditPost={onEditPost}
											onDeletePost={onRequestDeletePost}
											onViewChats={onViewChats}
											isActionLoading={
												isTableActionLoading
											}
											loadingChatPostId={
												loadingChatPostId
											}
										/>
									</PostsScrollView>
									<TablePagination
										rowsPerPageOptions={[]}
										component="div"
										count={totalPosts}
										rowsPerPage={rowsPerPage}
										page={page}
										onPageChange={handleChangePage}
										onRowsPerPageChange={
											handleChangeRowsPerPage
										}
									/>
								</>
							)}
						</Grid2>
					</Grid2>
				)}
			</div>
			<FacebookPostPopup
				mode="view"
				post={selectedPost}
				onClose={() => setSelectedPost(null)}
			/>
			<FacebookPostPopup
				mode={isCreateMode ? 'create' : 'edit'}
				post={editingPost}
				onChange={setEditingPost}
				onClose={onClosePostForm}
				onSave={onSavePost}
				isSaving={isSavingPost}
			/>
			<ConfirmationPopup
				open={Boolean(deletingPost)}
				title="Delete Facebook post?"
				message={`Are you sure you want to delete ${deletingPost?.name || 'this post'}?`}
				cancelButtonLabel="Cancel"
				okButtonLabel="Delete"
				cancelButtonStyle={{ color: '#7C7C7C' }}
				okButtonStyle={{
					backgroundColor: '#D92D20',
					color: '#FFFFFF',
					'&:hover': {
						backgroundColor: '#B42318',
					},
				}}
				onCancel={() => setDeletingPost(null)}
				onOk={onConfirmDeletePost}
				isLoading={isDeletingPost}
			/>
		</>
	);
}

const PrimaryButton = styled(Button)(() => ({
	backgroundColor: MAIN_THEME_COLOR,
	color: '#FFFFFF',
	padding: '6px 14px',
	textTransform: 'none',
	fontSize: '13px',
	fontWeight: 700,
	borderRadius: '6px',
	'&:hover': {
		backgroundColor: '#1F5E62',
	},
}));

const PostsScrollView = styled('div')(() => ({
	maxHeight: 'calc(100vh - 220px)',
	overflow: 'auto',
	width: '100%',
	'&::-webkit-scrollbar': {
		height: '6px',
		width: '6px',
	},
	'&::-webkit-scrollbar-track': {
		background: 'transparent',
	},
	'&::-webkit-scrollbar-thumb': {
		backgroundColor: '#D1D5DB',
		borderRadius: '3px',
	},
}));

export default FacebookManager;
